/**
 * Helpers de WhatsApp por deep-link (`wa.me`).
 *
 * NO usa la API de Meta: arma un link que abre el chat de WhatsApp del
 * profesional con el número (y opcionalmente un texto) precargado. Sin costo
 * por mensaje, sin envío de datos a Meta más allá de abrir la conversación.
 * Es el puente "tool antes de la integración real" (ver D8-WA en el roadmap).
 *
 * Módulo PURO y sin `server-only`: lo importan tanto Server Components como
 * Client Components (popover de agenda, ficha, reservas, página pública).
 */

/**
 * Normaliza un teléfono argentino a E.164 móvil best-effort (sin el `+`):
 * `549` + código de área + línea. Es el formato que exigen tanto `wa.me`
 * como la Cloud API de Meta para móviles AR. Devuelve `null` si no hay
 * dígitos utilizables.
 *
 * Endurecido para D8-WA (antes solo anteponía `54`): ahora resuelve el `9`
 * de móvil y limpia los prefijos de discado interno (`0` troncal y `15`
 * móvil) que el helper anterior dejaba pendientes. Asume número MÓVIL AR
 * (el caso del producto: recordatorios a pacientes). Sin tabla de códigos
 * de área la ubicación del `15` es heurística (ver `toArgentineMobileNational`).
 */
export function normalizeWhatsappPhone(
  phone: string | null | undefined,
): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, "");
  // Prefijo de acceso internacional: 00 54 11 ... → 54 11 ...
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length < 6) return null; // demasiado corto para ser un número real

  // Quitamos el código de país 54 si está; lo que queda es el número
  // nacional (con o sin los troncales de discado interno 0 / 15).
  const national = digits.startsWith("54") ? digits.slice(2) : digits;
  const canonical = toArgentineMobileNational(national);
  if (canonical.length < 6) return null;
  return `549${canonical}`;
}

/**
 * Reduce un número nacional argentino a su forma canónica de móvil
 * (`<área><línea>`, 10 dígitos para los casos típicos), quitando:
 *   - el `0` troncal de larga distancia (011…, 0351…);
 *   - el `9` de móvil (se re-agrega uniforme en `normalizeWhatsappPhone`);
 *   - el `15` de discado móvil que va DESPUÉS del código de área.
 *
 * El `15` no se puede ubicar con certeza sin una tabla de códigos de área
 * (varían de 2 a 4 dígitos), así que solo se quita cuando el número quedó
 * "largo" (12 dígitos) y hay un `15` justo tras un área de 2–4 dígitos —
 * el formato `<área:2-4><15><línea>`. Un número ya canónico (10 dígitos)
 * nunca se toca: el heurístico no puede romperlo.
 */
function toArgentineMobileNational(raw: string): string {
  let n = raw;
  if (n.startsWith("0")) n = n.slice(1); // troncal nacional
  if (n.startsWith("9")) n = n.slice(1); // 9 de móvil (se re-agrega luego)
  if (n.length === 12) {
    for (const areaLen of [2, 3, 4]) {
      if (n.slice(areaLen, areaLen + 2) === "15") {
        n = n.slice(0, areaLen) + n.slice(areaLen + 2);
        break;
      }
    }
  }
  return n;
}

/**
 * Construye la URL de WhatsApp (`https://wa.me/<intl>[?text=...]`).
 * Devuelve `null` si el teléfono no es utilizable (la UI no debe renderizar
 * el botón en ese caso — degradación elegante).
 *
 * El `message` se codifica con `encodeURIComponent`, así que no puede
 * inyectar parámetros ni romper la URL.
 */
export function buildWhatsappUrl(
  phone: string | null | undefined,
  message?: string | null,
): string | null {
  const intl = normalizeWhatsappPhone(phone);
  if (!intl) return null;
  const base = `https://wa.me/${intl}`;
  const text = message?.trim();
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

// ---------------------------------------------------------------------
// Plantillas de mensaje (puras). El agente IA y la UI las usan para
// precargar el texto. Voseo argentino, tono del producto, sin prometer
// nada que no sea cierto.
// ---------------------------------------------------------------------

/** Recordatorio de un turno próximo. */
export function reminderMessage(input: {
  personName: string;
  dateLabel: string;
  timeLabel: string;
}): string {
  return `Hola ${firstName(input.personName)}, te recuerdo tu turno del ${input.dateLabel} a las ${input.timeLabel}. ¿Confirmás que venís? ¡Gracias!`;
}

/** Recordatorio de un pago pendiente (sin importe; lo agrega quien lo use si quiere). */
export function paymentReminderMessage(input: {
  personName: string;
  amountLabel?: string | null;
}): string {
  const amount = input.amountLabel ? ` de ${input.amountLabel}` : "";
  return `Hola ${firstName(input.personName)}, ¿cómo estás? Te escribo por el saldo pendiente${amount}. Cualquier cosa quedo a disposición. ¡Gracias!`;
}

/** Propuesta de reprogramación. */
export function rescheduleMessage(input: { personName: string }): string {
  return `Hola ${firstName(input.personName)}, necesito reprogramar tu turno. ¿Qué día y horario te quedarían cómodos? ¡Gracias!`;
}

/** Mensaje neutro de apertura, sin contexto específico. */
export function blankMessage(input: { personName: string }): string {
  return `Hola ${firstName(input.personName)}, ¿cómo estás?`;
}

/** Primer nombre, para un saludo más natural. Cae al nombre completo si hace falta. */
function firstName(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return "";
  return trimmed.split(/\s+/)[0];
}
